/** @fileoverview Implements Fabrik app API calls through Platform auth. */

import type {
  FabrikAppDefinition,
  FabrikAppList,
  FabrikAppSummary,
  FabrikResolvedAppearance,
} from '../types/apps';
import { json } from './platformAuth';

export const listFabrikApps = (tenantId: string, signal?: AbortSignal) =>
  json<FabrikAppList>('/app/fabrik/apps?limit=100&offset=0', tenantId, { signal });

export const createFabrikApp = (
  tenantId: string,
  definition: FabrikAppDefinition,
  signal?: AbortSignal
) =>
  json<FabrikAppSummary>('/app/fabrik/apps', tenantId, {
    method: 'POST',
    body: JSON.stringify(definition),
    signal,
  });

export const switchFabrikAppSystem = (
  tenantId: string,
  appId: string,
  sourceSystemId: string,
  targetSystemId: string,
  target: 'draft' | 'published' = 'draft',
  signal?: AbortSignal
) =>
  json<FabrikAppSummary>(
    `/app/fabrik/apps/${encodeURIComponent(appId)}/system-binding?target=${target}`,
    tenantId,
    {
      method: 'PUT',
      body: JSON.stringify({ sourceSystemId, targetSystemId }),
      signal,
    }
  );

export const getFabrikApp = (tenantId: string, appId: string, signal?: AbortSignal) =>
  json<FabrikAppSummary>(`/app/fabrik/apps/${encodeURIComponent(appId)}`, tenantId, { signal });

export const updateFabrikAppDraft = (
  tenantId: string,
  appId: string,
  definition: FabrikAppDefinition
) =>
  json<FabrikAppSummary>(`/app/fabrik/apps/${encodeURIComponent(appId)}/draft`, tenantId, {
    method: 'PUT',
    body: JSON.stringify(definition),
  });

export const getFabrikAppAppearance = (tenantId: string, appId: string, signal?: AbortSignal) =>
  json<{ tenant: FabrikResolvedAppearance; effective: FabrikResolvedAppearance }>(
    `/app/fabrik/apps/${encodeURIComponent(appId)}/appearance`,
    tenantId,
    { signal }
  );

const MAX_APP_LOGO_BYTES = 1024 * 1024;
const APP_LOGO_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml'];
const PNG_SIGNATURE = [137, 80, 78, 71, 13, 10, 26, 10];

export const validateFabrikAppLogoFile = async (file: File) => {
  if (!APP_LOGO_TYPES.includes(file.type) || file.size > MAX_APP_LOGO_BYTES) {
    throw new Error('Choose a PNG, JPG, or SVG image no larger than 1 MB.');
  }

  const bytes = new Uint8Array(await file.arrayBuffer());
  const matchesType =
    (file.type === 'image/png' &&
      bytes.length >= PNG_SIGNATURE.length &&
      bytes
        .subarray(0, PNG_SIGNATURE.length)
        .every((byte, index) => byte === PNG_SIGNATURE[index])) ||
    (file.type === 'image/jpeg' && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) ||
    (file.type === 'image/svg+xml' &&
      /^(?:\uFEFF)?\s*(?:<\?xml\b[\s\S]*?\?>\s*)?(?:<!--[\s\S]*?-->\s*)*(?:<!DOCTYPE\s+svg\b[^>]*>\s*)*<svg(?:\s|>)/i.test(
        new TextDecoder().decode(bytes)
      ));

  if (!matchesType) {
    throw new Error('The file contents do not match a PNG, JPG, or SVG image.');
  }
};

export const uploadFabrikAppLogo = async (tenantId: string, appId: string, file: File) => {
  await validateFabrikAppLogoFile(file);
  const base64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = () => reject(new Error('Unable to read this image.'));
    reader.readAsDataURL(file);
  });
  return json<{ logoKey: string }>(`/app/fabrik/apps/${encodeURIComponent(appId)}/logo`, tenantId, {
    method: 'POST',
    body: JSON.stringify({ contentType: file.type, base64 }),
  });
};

export const renameFabrikApp = (
  tenantId: string,
  appId: string,
  name: string,
  preserveOldUrl: boolean
) =>
  json<FabrikAppSummary>(`/app/fabrik/apps/${encodeURIComponent(appId)}/name`, tenantId, {
    method: 'PUT',
    body: JSON.stringify({ name, preserveOldUrl }),
  });

export const publishFabrikApp = (
  tenantId: string,
  appId: string,
  systemMappings?: Record<string, string>,
  signal?: AbortSignal
) =>
  json<FabrikAppSummary>(`/app/fabrik/apps/${encodeURIComponent(appId)}/publish`, tenantId, {
    method: 'POST',
    ...(systemMappings && Object.keys(systemMappings).length
      ? { body: JSON.stringify({ systemMappings }) }
      : {}),
    signal,
  });

export const deleteFabrikApp = (tenantId: string, appId: string, signal?: AbortSignal) =>
  json<void>(`/app/fabrik/apps/${encodeURIComponent(appId)}`, tenantId, {
    method: 'DELETE',
    signal,
  });
